import { ArrowLeft, FileText, Shield, AlertTriangle } from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';

export function TermsPage() {
  const { setCurrentView } = useUIStore();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button variant="ghost" size="icon" onClick={() => setCurrentView('chat')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold">AGB und Richtlinien</h1>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          {/* Wichtiger Hinweis */}
          <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <AlertTriangle className="h-5 w-5" />
                Wichtiger Hinweis zum Datenschutz
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-orange-700 dark:text-orange-400 font-medium">
                Bitte geben Sie keine persönlichen Daten in den Chat ein. Dies umfasst insbesondere:
              </p>
              <ul className="list-disc list-inside mt-2 text-orange-600 dark:text-orange-400/80 space-y-1">
                <li>Namen von Patienten, Bewohnern oder Angehörigen</li>
                <li>Adressen, Telefonnummern oder E-Mail-Adressen</li>
                <li>Geburtsdaten oder Sozialversicherungsnummern</li>
                <li>Medizinische Diagnosen mit Personenbezug</li>
                <li>Interne Mitarbeiterinformationen</li>
                <li>Vertrauliche Einrichtungsdaten</li>
              </ul>
              <p className="mt-3 text-orange-600 dark:text-orange-400/80">
                Die KI-Antworten dienen ausschließlich zu Informationszwecken und ersetzen keine professionelle medizinische oder pflegerische Beratung.
              </p>
            </CardContent>
          </Card>

          {/* Allgemeine Geschäftsbedingungen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Allgemeine Geschäftsbedingungen (AGB)
              </CardTitle>
              <CardDescription>
                Stand: Februar 2026
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <section>
                <h3 className="font-semibold text-lg mb-2">§ 1 Geltungsbereich</h3>
                <p className="text-muted-foreground">
                  Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Anwendung "KI für Pflege"
                  (nachfolgend "Dienst" genannt). Mit der Registrierung und Nutzung des Dienstes erkennen
                  Sie diese AGB an.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 2 Leistungsbeschreibung</h3>
                <p className="text-muted-foreground mb-2">
                  Der Dienst bietet Pflegekräften die Möglichkeit, Fragen zu pflegerelevanten Themen zu stellen
                  und Antworten durch künstliche Intelligenz zu erhalten. Der Dienst umfasst:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Chat-basierte Kommunikation mit KI-Unterstützung</li>
                  <li>Speicherung von Konversationsverläufen</li>
                  <li>Benutzerkontenverwaltung</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 3 Registrierung und Benutzerkonto</h3>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Für die Nutzung des Dienstes ist eine Registrierung erforderlich.</li>
                  <li>Sie sind verpflichtet, bei der Registrierung wahrheitsgemäße Angaben zu machen.</li>
                  <li>Die Zugangsdaten sind vertraulich zu behandeln und vor dem Zugriff Dritter zu schützen.</li>
                  <li>Sie sind für alle Aktivitäten verantwortlich, die unter Ihrem Benutzerkonto stattfinden.</li>
                </ol>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 4 Nutzungspflichten</h3>
                <p className="text-muted-foreground mb-2">Bei der Nutzung des Dienstes verpflichten Sie sich:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Keine personenbezogenen Daten von Dritten (Patienten, Bewohner, Kollegen) einzugeben</li>
                  <li>Den Dienst nicht für rechtswidrige oder missbräuchliche Zwecke zu nutzen</li>
                  <li>Keine Inhalte zu übermitteln, die gegen geltendes Recht verstoßen</li>
                  <li>Die KI-generierten Antworten kritisch zu prüfen und nicht blind zu übernehmen</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 5 Geistiges Eigentum</h3>
                <p className="text-muted-foreground">
                  Alle Rechte an der Anwendung, einschließlich Software, Design und Dokumentation,
                  verbleiben beim Betreiber. Die Nutzung berechtigt nicht zur Vervielfältigung,
                  Verbreitung oder Veränderung der Anwendung.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 6 Verfügbarkeit</h3>
                <p className="text-muted-foreground">
                  Der Betreiber bemüht sich um eine hohe Verfügbarkeit des Dienstes, kann jedoch keine
                  ununterbrochene Verfügbarkeit garantieren. Wartungsarbeiten und technische Störungen
                  können zu vorübergehenden Einschränkungen führen.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 7 Kündigung</h3>
                <p className="text-muted-foreground">
                  Sie können Ihr Benutzerkonto jederzeit in den Einstellungen löschen. Der Betreiber
                  behält sich das Recht vor, Benutzerkonten bei Verstößen gegen diese AGB zu sperren
                  oder zu löschen.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">§ 8 Änderungen der AGB</h3>
                <p className="text-muted-foreground">
                  Der Betreiber behält sich vor, diese AGB jederzeit zu ändern. Änderungen werden
                  den Nutzern in geeigneter Weise mitgeteilt. Die fortgesetzte Nutzung nach
                  Inkrafttreten der Änderungen gilt als Zustimmung.
                </p>
              </section>
            </CardContent>
          </Card>

          {/* Haftungsausschluss */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Shield className="h-5 w-5" />
                Haftungsausschluss
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <section>
                <h3 className="font-semibold mb-2">Keine medizinische oder pflegerische Beratung</h3>
                <p className="text-muted-foreground">
                  Die durch den Dienst bereitgestellten Informationen und Antworten dienen ausschließlich
                  allgemeinen Informationszwecken. Sie stellen keine medizinische, pflegerische oder
                  rechtliche Beratung dar und ersetzen nicht die Konsultation qualifizierter Fachkräfte.
                </p>
              </section>

              <section>
                <h3 className="font-semibold mb-2">Keine Gewährleistung für Richtigkeit</h3>
                <p className="text-muted-foreground">
                  Obwohl wir uns um Genauigkeit bemühen, übernehmen wir keine Gewährleistung für die
                  Richtigkeit, Vollständigkeit oder Aktualität der KI-generierten Antworten.
                  KI-Systeme können Fehler machen oder veraltete Informationen liefern.
                </p>
              </section>

              <section>
                <h3 className="font-semibold mb-2">Haftungsbeschränkung</h3>
                <p className="text-muted-foreground">
                  Der Betreiber haftet nicht für Schäden, die aus der Nutzung oder Nichtnutzung des
                  Dienstes entstehen, es sei denn, es liegt Vorsatz oder grobe Fahrlässigkeit vor.
                  Dies gilt insbesondere für:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li>Schäden durch fehlerhafte oder unvollständige KI-Antworten</li>
                  <li>Entscheidungen, die auf Basis der bereitgestellten Informationen getroffen werden</li>
                  <li>Datenverlust oder Systemausfälle</li>
                  <li>Unbefugten Zugriff auf Benutzerkonten durch Dritte</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-2">Eigenverantwortung</h3>
                <p className="text-muted-foreground">
                  Die Nutzung des Dienstes erfolgt auf eigene Verantwortung. Nutzer sind angehalten,
                  alle erhaltenen Informationen kritisch zu prüfen und bei Unsicherheiten professionellen
                  Rat einzuholen.
                </p>
              </section>
            </CardContent>
          </Card>

          {/* Datenschutz */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Datenschutzrichtlinien
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <section>
                <h3 className="font-semibold text-lg mb-2">Erhobene Daten</h3>
                <p className="text-muted-foreground mb-2">Im Rahmen der Nutzung werden folgende Daten erhoben:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>E-Mail-Adresse (für die Anmeldung)</li>
                  <li>Optionaler Anzeigename</li>
                  <li>Chat-Verläufe und Nachrichten</li>
                  <li>Technische Nutzungsdaten (z.B. Zeitstempel)</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">Verwendungszweck</h3>
                <p className="text-muted-foreground">
                  Ihre Daten werden ausschließlich für die Bereitstellung und Verbesserung des Dienstes
                  verwendet. Eine Weitergabe an Dritte erfolgt nicht, es sei denn, dies ist gesetzlich
                  vorgeschrieben oder zur Erbringung des Dienstes erforderlich.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">Datensicherheit</h3>
                <p className="text-muted-foreground">
                  Wir setzen technische und organisatorische Maßnahmen ein, um Ihre Daten vor unbefugtem
                  Zugriff zu schützen. Die Datenübertragung erfolgt verschlüsselt. Passwörter werden
                  gehasht gespeichert.
                </p>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">Ihre Rechte</h3>
                <p className="text-muted-foreground mb-2">Sie haben das Recht auf:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Auskunft über Ihre gespeicherten Daten</li>
                  <li>Berichtigung unrichtiger Daten</li>
                  <li>Löschung Ihrer Daten (über die Kontoeinstellungen)</li>
                  <li>Einschränkung der Verarbeitung</li>
                  <li>Widerspruch gegen die Verarbeitung</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-lg mb-2">Speicherdauer</h3>
                <p className="text-muted-foreground">
                  Ihre Daten werden gespeichert, solange Ihr Benutzerkonto besteht. Nach Löschung
                  des Kontos werden alle personenbezogenen Daten unwiderruflich entfernt.
                </p>
              </section>
            </CardContent>
          </Card>

          {/* Nutzungsrichtlinien */}
          <Card>
            <CardHeader>
              <CardTitle>Nutzungsrichtlinien</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <section>
                <h3 className="font-semibold mb-2">Zulässige Nutzung</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Allgemeine Fragen zu pflegerischen Themen</li>
                  <li>Informationsrecherche zu Pflegestandards und -verfahren</li>
                  <li>Unterstützung bei der Dokumentation (ohne Personenbezug)</li>
                  <li>Weiterbildung und Wissenserweiterung</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-2">Unzulässige Nutzung</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Eingabe von personenbezogenen Daten</li>
                  <li>Nutzung für Diagnosen ohne ärztliche Konsultation</li>
                  <li>Verbreitung der Inhalte ohne Quellenprüfung</li>
                  <li>Missbrauch des Systems für nicht-pflegerische Zwecke</li>
                </ul>
              </section>
            </CardContent>
          </Card>

          {/* Kontakt */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Kontakt</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Bei Fragen zu diesen AGB und Richtlinien oder zum Datenschutz wenden Sie sich bitte
                an den Support über die "Fehler melden"-Funktion in der Anwendung.
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground pb-6">
            Stand: Februar 2026
          </p>
        </div>
      </ScrollArea>
    </div>
  );
}
